/**
 * Camada de dados.
 *
 * Chaves hierárquicas — `['group', id, 'games']` — para que invalidar
 * `['group', id]` derrube tudo daquele grupo de uma vez. É o que mantém a tela
 * coerente depois de uma ação sem precisar recarregar nada na mão.
 */

import type {
  AdminDashboard,
  AppNotification,
  Expense,
  FinancialOverview,
  FinancialReport,
  FinancialTransaction,
  GameDetail,
  GameSummary,
  GroupBillingDetail,
  GroupBillingStatus,
  GroupDetail,
  GroupMember,
  GroupSummary,
  GroupWhatsappSettings,
  MembershipFee,
  MyRatingSummary,
  PaginatedResult,
  PixCharge,
  PlatformBillingOverview,
  PlatformBillingSettings,
  PlatformInvoice,
  PlatformInvoiceCharge,
  PlatformInvoiceRow,
  PlayerStatistics,
  RankingEntry,
  RankingMetric,
  RatingBallot,
  TeamBalanceReport,
} from '@nossorachao/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from './api';

export const keys = {
  dashboard: ['me', 'dashboard'] as const,
  notifications: ['notifications'] as const,
  unread: ['notifications', 'unread'] as const,
  groups: ['groups'] as const,
  group: (id: string) => ['group', id] as const,
  members: (id: string) => ['group', id, 'members'] as const,
  games: (id: string, scope: string) => ['group', id, 'games', scope] as const,
  invites: (id: string) => ['group', id, 'invites'] as const,
  ranking: (id: string, metric: string) => ['group', id, 'ranking', metric] as const,
  financial: (id: string) => ['group', id, 'financial'] as const,
  fees: (id: string) => ['group', id, 'fees'] as const,
  expenses: (id: string) => ['group', id, 'expenses'] as const,
  transactions: (id: string) => ['group', id, 'financial', 'transactions'] as const,
  report: (id: string, month: string) => ['group', id, 'financial', 'report', month] as const,
  whatsapp: (id: string) => ['group', id, 'whatsapp'] as const,
  adminDash: (id: string) => ['group', id, 'admin-dashboard'] as const,
  myRating: (id: string) => ['group', id, 'my-rating'] as const,
  memberStats: (g: string, m: string) => ['group', g, 'member', m, 'stats'] as const,
  billingStatus: (id: string) => ['group', id, 'billing', 'status'] as const,
  billing: (id: string) => ['group', id, 'billing'] as const,
  platformBilling: ['platform', 'billing'] as const,
  platformInvoices: (filter: string) => ['platform', 'billing', 'invoices', filter] as const,
  game: (id: string) => ['game', id] as const,
  ballot: (id: string) => ['game', id, 'ballot'] as const,
};

/** 4xx não se resolve repetindo; falha de rede e 5xx, sim. */
export const retry = (count: number, error: unknown) =>
  error instanceof ApiError && !error.isTransient ? false : count < 2;

// -----------------------------------------------------------------------------
// Home
// -----------------------------------------------------------------------------

export interface DashboardData {
  nextGame: GameSummary | null;
  pendingRatingsCount: number;
  recentGames: GameSummary[];
}

export function useDashboard() {
  return useQuery({
    queryKey: keys.dashboard,
    queryFn: () => api.get<DashboardData>('/me/dashboard'),
    retry,
    staleTime: 30_000,
  });
}

// -----------------------------------------------------------------------------
// Grupos
// -----------------------------------------------------------------------------

export function useGroups() {
  return useQuery({
    queryKey: keys.groups,
    queryFn: () => api.get<GroupSummary[]>('/groups'),
    retry,
    staleTime: 60_000,
  });
}

export function useGroup(groupId: string) {
  return useQuery({
    queryKey: keys.group(groupId),
    queryFn: () => api.get<GroupDetail>(`/groups/${groupId}`),
    retry,
    enabled: Boolean(groupId),
  });
}

export function useMembers(groupId: string) {
  return useQuery({
    queryKey: keys.members(groupId),
    queryFn: () => api.get<GroupMember[]>(`/groups/${groupId}/members`),
    retry,
    enabled: Boolean(groupId),
  });
}

export function useCreateGroup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<GroupDetail>('/groups', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.groups }),
  });
}

/**
 * Dados da pelada e configurações vão em rotas separadas na API (a chave Pix
 * mora nas configurações, criptografada). Invalidar `['group', id]` derruba
 * detalhe, financeiro e partidas — o horário e a mensalidade aparecem em
 * todos eles.
 */
export function useUpdateGroup(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.patch<GroupDetail>(`/groups/${groupId}`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.group(groupId) });
      void client.invalidateQueries({ queryKey: keys.groups });
    },
  });
}

export function useUpdateGroupSettings(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/groups/${groupId}/settings`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.group(groupId) }),
  });
}

export function useGroupWhatsapp(groupId: string) {
  return useQuery({
    queryKey: keys.whatsapp(groupId),
    queryFn: () => api.get<GroupWhatsappSettings>(`/groups/${groupId}/whatsapp`),
    retry,
    enabled: Boolean(groupId),
    // O vínculo acontece no celular, fora da tela: recarregar de tempos em
    // tempos mostra "vinculado" sem a pessoa precisar dar F5.
    refetchInterval: 15_000,
  });
}

/** `action` escolhe a rota; todas devolvem o estado atualizado. */
export function useGroupWhatsappAction(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (
      input:
        | { action: 'update'; body: { enabled?: boolean; events?: Record<string, string> } }
        | { action: 'unlink' }
        | { action: 'test' },
    ) => {
      const base = `/groups/${groupId}/whatsapp`;
      if (input.action === 'update') return api.patch<GroupWhatsappSettings>(base, input.body);
      if (input.action === 'unlink') return api.post<GroupWhatsappSettings>(`${base}/unlink`);
      // O teste não muda o estado (204): devolve null para não sobrescrever o cache.
      return api.post<void>(`${base}/test`).then(() => null);
    },
    onSuccess: (data: GroupWhatsappSettings | null) => {
      if (data) client.setQueryData(keys.whatsapp(groupId), data);
    },
  });
}

export function useTransferOwnership(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { newOwnerMemberId: string; password: string }) =>
      api.post(`/groups/${groupId}/transfer-ownership`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.group(groupId) });
      void client.invalidateQueries({ queryKey: keys.groups });
    },
  });
}

export function useCreateInvite(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (expiresInDays: number) =>
      api.post<{ code: string; url: string | null }>(`/groups/${groupId}/invites`, {
        expiresInDays,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.invites(groupId) }),
  });
}

export function useAcceptInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ groupId: string; groupName: string; pendingApproval: boolean }>(
        '/invites/accept',
        { code },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.groups });
      void client.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

export function useUpdateMemberSkill(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { memberId: string; skillRating: number; reason: string }) =>
      api.patch(`/groups/${groupId}/members/${input.memberId}/skill`, {
        skillRating: input.skillRating,
        reason: input.reason,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.group(groupId) }),
  });
}

export function useChangeMemberRole(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { memberId: string; role: 'ADMIN' | 'MEMBER' }) =>
      api.patch(`/groups/${groupId}/members/${input.memberId}/role`, { role: input.role }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.group(groupId) }),
  });
}

// -----------------------------------------------------------------------------
// Partidas
// -----------------------------------------------------------------------------

export function useGames(groupId: string, scope: 'upcoming' | 'past' = 'upcoming') {
  return useQuery({
    queryKey: keys.games(groupId, scope),
    queryFn: () =>
      api.get<PaginatedResult<GameSummary>>(`/groups/${groupId}/games`, { query: { scope } }),
    retry,
    enabled: Boolean(groupId),
  });
}

export function useGame(gameId: string) {
  return useQuery({
    queryKey: keys.game(gameId),
    queryFn: () => api.get<GameDetail>(`/games/${gameId}`),
    retry,
    enabled: Boolean(gameId),
  });
}

export function useCreateGame(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { scheduledAt: string; notes?: string }) =>
      api.post<GameDetail>(`/groups/${groupId}/games`, { ...input, announce: true }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.group(groupId) });
      void client.invalidateQueries({ queryKey: keys.groups });
    },
  });
}

/**
 * Confirmar/recusar presença.
 *
 * Sem update otimista de propósito: a resposta pode divergir do pedido —
 * confirmar numa pelada lotada devolve WAITLIST. Mostrar "confirmado" e
 * corrigir logo depois seria pior que esperar 200 ms.
 */
export function useRespondAttendance(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (status: 'CONFIRMED' | 'DECLINED' | 'MAYBE') =>
      api.post<{
        status: string;
        waitlistPosition: number | null;
        confirmedCount: number;
        wentToWaitlist: boolean;
      }>(`/games/${gameId}/attendance`, { status }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.game(gameId) });
      void client.invalidateQueries({ queryKey: keys.dashboard });
      void client.invalidateQueries({ queryKey: keys.groups });
    },
  });
}

export function useGenerateTeams(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { seed?: number; allowRepeatLineup?: boolean }) =>
      api.post<TeamBalanceReport>(`/games/${gameId}/teams/generate`, {
        allowRepeatLineup: false,
        ...input,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.game(gameId) }),
  });
}

export function useSaveTeams(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { teams: unknown[] }) => api.put(`/games/${gameId}/teams`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.game(gameId) }),
  });
}

export function useAddGuest(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ id: string }>(`/games/${gameId}/guests`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.game(gameId) }),
  });
}

export function useRemoveGuest(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (guestId: string) => api.delete(`/games/${gameId}/guests/${guestId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.game(gameId) }),
  });
}

/**
 * Admin tira um membro da partida: vira DECLINED e, se ele estava dentro, a
 * API promove o primeiro da fila de espera.
 */
export function useRemovePlayer(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      api.post(`/games/${gameId}/attendance/member`, {
        memberId,
        status: 'DECLINED',
        reason: 'Removido pelo administrador',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.game(gameId) });
      void client.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

export function useRegisterMatch(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ id: string }>(`/games/${gameId}/matches`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.game(gameId) }),
  });
}

export function useFinishGame(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/games/${gameId}/finish`, { openRatings: true }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.game(gameId) });
      void client.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

// -----------------------------------------------------------------------------
// Avaliações
// -----------------------------------------------------------------------------

export function useBallot(gameId: string, enabled = true) {
  return useQuery({
    queryKey: keys.ballot(gameId),
    queryFn: () => api.get<RatingBallot>(`/games/${gameId}/ratings/ballot`),
    enabled: enabled && Boolean(gameId),
    // Os tokens são reemitidos a cada consulta; cachear invalidaria a cédula.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function useSubmitRatings(gameId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ratings: Array<{ ballotToken: string; score: number }>) =>
      api.post<{ submitted: number; skipped: number }>(`/games/${gameId}/ratings`, { ratings }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.game(gameId) });
      void client.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

export function useMyRating(groupId: string) {
  return useQuery({
    queryKey: keys.myRating(groupId),
    queryFn: () => api.get<MyRatingSummary>(`/groups/${groupId}/me/rating`),
    retry,
    enabled: Boolean(groupId),
  });
}

export function useMemberStats(groupId: string, memberId: string) {
  return useQuery({
    queryKey: keys.memberStats(groupId, memberId),
    queryFn: () => api.get<PlayerStatistics>(`/groups/${groupId}/members/${memberId}/statistics`),
    retry,
    enabled: Boolean(groupId && memberId),
  });
}

export function useRanking(groupId: string, metric: RankingMetric) {
  return useQuery({
    queryKey: keys.ranking(groupId, metric),
    queryFn: () => api.get<RankingEntry[]>(`/groups/${groupId}/ranking`, { query: { metric } }),
    retry,
    enabled: Boolean(groupId),
  });
}

// -----------------------------------------------------------------------------
// Financeiro
// -----------------------------------------------------------------------------

export function useFinancial(groupId: string) {
  return useQuery({
    queryKey: keys.financial(groupId),
    queryFn: () => api.get<FinancialOverview>(`/groups/${groupId}/financial`),
    retry,
    enabled: Boolean(groupId),
  });
}

export function useFees(groupId: string, referenceMonth?: string) {
  return useQuery({
    queryKey: [...keys.fees(groupId), referenceMonth ?? 'all'],
    queryFn: () =>
      api.get<MembershipFee[]>(`/groups/${groupId}/financial/fees`, {
        query: { referenceMonth },
      }),
    retry,
    enabled: Boolean(groupId),
  });
}

export function useMyFees(groupId: string) {
  return useQuery({
    queryKey: [...keys.fees(groupId), 'mine'],
    queryFn: () => api.get<MembershipFee[]>(`/groups/${groupId}/financial/fees/mine`),
    retry,
    enabled: Boolean(groupId),
  });
}

export function useExpenses(groupId: string) {
  return useQuery({
    queryKey: keys.expenses(groupId),
    queryFn: () => api.get<PaginatedResult<Expense>>(`/groups/${groupId}/financial/expenses`),
    retry,
    enabled: Boolean(groupId),
  });
}

export function useCreateExpense(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ id: string }>(`/groups/${groupId}/financial/expenses`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.expenses(groupId) });
      void client.invalidateQueries({ queryKey: keys.financial(groupId) });
    },
  });
}

/** Extrato do caixa. Fica sob a chave do financeiro: qualquer lançamento o invalida junto. */
export function useTransactions(groupId: string, enabled = true) {
  return useQuery({
    queryKey: keys.transactions(groupId),
    queryFn: () =>
      api.get<PaginatedResult<FinancialTransaction>>(
        `/groups/${groupId}/financial/transactions?limit=10`,
      ),
    retry,
    enabled: Boolean(groupId) && enabled,
  });
}

export function useFinancialReport(groupId: string, month: string, enabled = true) {
  return useQuery({
    queryKey: keys.report(groupId, month),
    queryFn: () =>
      api.get<FinancialReport>(`/groups/${groupId}/financial/report`, { query: { month } }),
    retry,
    enabled: Boolean(groupId) && enabled,
  });
}

export function useCreateIncome(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { description: string; amountCents: number; receivedAt: string }) =>
      api.post<{ id: string }>(`/groups/${groupId}/financial/incomes`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.financial(groupId) }),
  });
}

export function useGenerateFees(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (referenceMonth: string) =>
      api.post<{ created: number; skipped: number; totalCents: number }>(
        `/groups/${groupId}/financial/fees/generate`,
        { referenceMonth, createPixCharges: false },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.fees(groupId) });
      void client.invalidateQueries({ queryKey: keys.financial(groupId) });
    },
  });
}

export function useCreateCharge(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: 'MONTHLY' | 'EXTRA';
      referenceMonth?: string;
      description?: string;
      amountCents: number;
      dueDate: string;
      memberIds?: string[];
    }) =>
      api.post<{ created: number; skipped: number; totalCents: number }>(
        `/groups/${groupId}/financial/fees/charge`,
        input,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.fees(groupId) });
      void client.invalidateQueries({ queryKey: keys.financial(groupId) });
      void client.invalidateQueries({ queryKey: keys.members(groupId) });
    },
  });
}

export function useCreatePixCharge(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (feeId: string) =>
      api.post<PixCharge>(`/groups/${groupId}/financial/fees/${feeId}/pix`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.fees(groupId) }),
  });
}

/** Baixa manual — no Pix direto na chave, é o admin quem confirma. */
export function useRegisterManualPayment(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      feeId,
      ...input
    }: {
      feeId: string;
      method: 'PIX' | 'CASH' | 'TRANSFER' | 'OTHER';
      amountCents: number;
      paidAt: string;
    }) =>
      api.post<{ id: string }>(`/groups/${groupId}/financial/fees/${feeId}/manual-payment`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.fees(groupId) });
      void client.invalidateQueries({ queryKey: keys.financial(groupId) });
    },
  });
}

/** Participante envia o comprovante — a mensalidade fica paga na hora. */
export function useSubmitReceipt(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ feeId, file }: { feeId: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<{ id: string }>(`/groups/${groupId}/financial/fees/${feeId}/receipt`, form);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.fees(groupId) });
      void client.invalidateQueries({ queryKey: keys.financial(groupId) });
    },
  });
}

/** Admin conferiu a conta e o Pix não entrou. */
export function useRevertPayment(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: string; reason?: string }) =>
      api.post<{ id: string }>(`/groups/${groupId}/financial/payments/${paymentId}/revert`, {
        reason: reason || null,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.fees(groupId) });
      void client.invalidateQueries({ queryKey: keys.financial(groupId) });
    },
  });
}

/** Exclui um lançamento errado do caixa (entrada, ajuste ou despesa). */
export function useDeleteTransaction(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (transactionId: string) =>
      api.delete<void>(`/groups/${groupId}/financial/transactions/${transactionId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.financial(groupId) });
      void client.invalidateQueries({ queryKey: keys.expenses(groupId) });
      void client.invalidateQueries({ queryKey: keys.adminDash(groupId) });
    },
  });
}

/** Cancela uma cobrança lançada por engano (só as que não foram pagas). */
export function useCancelFee(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ feeId, reason }: { feeId: string; reason?: string }) =>
      api.patch<{ id: string }>(`/groups/${groupId}/financial/fees/${feeId}`, {
        status: 'CANCELED',
        reason: reason || null,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.fees(groupId) });
      void client.invalidateQueries({ queryKey: keys.financial(groupId) });
      void client.invalidateQueries({ queryKey: keys.adminDash(groupId) });
    },
  });
}

/** Abre o comprovante numa aba — a rota exige token, então vem como blob. */
export async function openReceipt(groupId: string, paymentId: string): Promise<void> {
  // A aba é aberta antes do await: depois dele o navegador trata como popup.
  const tab = window.open('', '_blank');
  const blob = await api.blob(`/groups/${groupId}/financial/payments/${paymentId}/receipt`);
  const url = URL.createObjectURL(blob);
  if (tab) tab.location.href = url;
  else window.location.assign(url);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function useAdminDashboard(groupId: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.adminDash(groupId),
    queryFn: () => api.get<AdminDashboard>(`/groups/${groupId}/dashboard/admin`),
    retry,
    enabled: enabled && Boolean(groupId),
  });
}

// -----------------------------------------------------------------------------
// Notificações
// -----------------------------------------------------------------------------

export function useNotifications() {
  return useQuery({
    queryKey: keys.notifications,
    queryFn: () => api.get<PaginatedResult<AppNotification>>('/notifications'),
    retry,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: keys.unread,
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    retry,
    refetchInterval: 60_000,
  });
}

export function useMarkAllRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.notifications });
      void client.invalidateQueries({ queryKey: keys.unread });
    },
  });
}

// -----------------------------------------------------------------------------
// Assinatura da plataforma — lado do grupo
// -----------------------------------------------------------------------------

export function useBillingStatus(groupId: string) {
  return useQuery({
    queryKey: keys.billingStatus(groupId),
    queryFn: () => api.get<GroupBillingStatus>(`/groups/${groupId}/billing/status`),
    retry,
    staleTime: 60_000,
    enabled: Boolean(groupId),
  });
}

export function useGroupBilling(groupId: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.billing(groupId),
    queryFn: () => api.get<GroupBillingDetail>(`/groups/${groupId}/billing`),
    retry,
    enabled: enabled && Boolean(groupId),
  });
}

export function useBillingCharge(groupId: string) {
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<PlatformInvoiceCharge>(`/groups/${groupId}/billing/invoices/${invoiceId}/pix`),
  });
}

export function useSubmitBillingReceipt(groupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, file }: { invoiceId: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<PlatformInvoice>(
        `/groups/${groupId}/billing/invoices/${invoiceId}/receipt`,
        form,
      );
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.billing(groupId) }),
  });
}

// -----------------------------------------------------------------------------
// Assinatura da plataforma — painel
// -----------------------------------------------------------------------------

export function usePlatformBilling(enabled = true) {
  return useQuery({
    queryKey: keys.platformBilling,
    queryFn: () => api.get<PlatformBillingOverview>('/platform/billing'),
    retry,
    enabled,
  });
}

export function usePlatformInvoices(
  filter: { status?: string; groupId?: string; month?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: keys.platformInvoices(JSON.stringify(filter)),
    queryFn: () => api.get<PlatformInvoiceRow[]>('/platform/billing/invoices', { query: filter }),
    retry,
    enabled,
  });
}

/** Toda ação do painel muda totais e linhas: invalida o painel inteiro. */
function usePlatformMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.platformBilling }),
  });
}

export function useUpdatePlatformSettings() {
  return usePlatformMutation((input: Record<string, unknown>) =>
    api.patch<PlatformBillingSettings>('/platform/billing/settings', input),
  );
}

export function useUpdateGroupBilling() {
  return usePlatformMutation(
    ({ groupId, ...input }: { groupId: string } & Record<string, unknown>) =>
      api.patch(`/platform/billing/groups/${groupId}`, input),
  );
}

export function useGeneratePlatformInvoices() {
  return usePlatformMutation((input: { referenceMonth: string; groupId?: string }) =>
    api.post<{ created: number; skipped: number; totalCents: number }>(
      '/platform/billing/invoices/generate',
      input,
    ),
  );
}

export function useReviewPlatformInvoice() {
  return usePlatformMutation(
    ({
      invoiceId,
      action,
      ...body
    }: {
      invoiceId: string;
      action: 'confirm' | 'reject' | 'waive';
      method?: string;
      notes?: string;
      reason?: string;
    }) => api.post<PlatformInvoice>(`/platform/billing/invoices/${invoiceId}/${action}`, body),
  );
}

export async function openPlatformReceipt(invoiceId: string): Promise<void> {
  const tab = window.open('', '_blank');
  const blob = await api.blob(`/platform/billing/invoices/${invoiceId}/receipt`);
  const url = URL.createObjectURL(blob);
  if (tab) tab.location.href = url;
  else window.location.assign(url);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
